import { useState, useEffect, useRef } from 'react';
import { useSpring, animated } from '@react-spring/web';
import {
  Github, AlertCircle, GitBranch, ExternalLink,
  Loader2, CheckCircle2, ChevronDown,
} from 'lucide-react';
import { StatusChip } from '@ncoder/ui';
import { useGitConnection }        from '@/hooks/git/useGitConnection';
import { useGitSelectedRepo }      from '@/hooks/git/useGitSelectedRepo';
import { useDefaultGitBranch }     from '@/hooks/git/useDefaultGitBranch';
import { useGitBranches }          from '@/hooks/git/useGitBranches';
import { useSetDefaultGitBranch }  from '@/hooks/git/useSetDefaultGitBranch';
import type { GitBranch, GitRepo } from '@/services/git/types';

// ── Root ──────────────────────────────────────────────────────────────────────

export function GitStatusButton() {
  const { data: connection, isLoading: loadingConnection } = useGitConnection();
  const isConnected = !!connection;

  const { data: selectedRepo, isLoading: loadingRepo } = useGitSelectedRepo({
    enabled: isConnected,
  });

  const { data: configuredDefaultBranch } = useDefaultGitBranch();
  const effectiveDefaultBranch = configuredDefaultBranch?.name ?? selectedRepo?.default_branch;

  const isLoading = loadingConnection || (isConnected && loadingRepo);
  const status = !isConnected ? 'disconnected' : !selectedRepo ? 'no-repo' : 'connected';

  const label = isLoading
    ? 'Checking...'
    : status === 'disconnected' ? 'Not connected'
    : status === 'no-repo'     ? 'No repo linked'
    : (selectedRepo?.full_name ?? 'Connected');

  const variant =
    status === 'connected' ? 'success' :
    status === 'no-repo'   ? 'warning' :
    'default';

  return (
    <StatusChip
      variant={variant}
      icon={
        isLoading
          ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
          : <Github className="h-3 w-3 shrink-0" />
      }
      label={label}
      popoverWidth={320}
    >
      {!isLoading && status === 'disconnected' && <DisconnectedPanel />}
      {!isLoading && status === 'no-repo'      && <NoRepoPanel />}
      {!isLoading && status === 'connected'    && (
        <ConnectedPanel
          repo={selectedRepo!}
          effectiveDefaultBranch={effectiveDefaultBranch}
          configuredDefaultBranch={configuredDefaultBranch ?? null}
        />
      )}
    </StatusChip>
  );
}

// ── Panel: not connected ──────────────────────────────────────────────────────

function DisconnectedPanel() {
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 h-7 w-7 rounded-md bg-muted flex items-center justify-center shrink-0">
          <Github className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <div>
          <p className="text-xs font-medium text-foreground">GitHub not connected</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
            Connect your GitHub account to version control your Salesforce metadata.
          </p>
        </div>
      </div>
      <a
        href="/studio/git"
        className="flex items-center justify-center gap-1.5 w-full px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
      >
        <Github className="h-3 w-3" />
        Connect GitHub
      </a>
    </div>
  );
}

// ── Panel: no repo selected ───────────────────────────────────────────────────

function NoRepoPanel() {
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 h-7 w-7 rounded-md bg-amber-500/10 flex items-center justify-center shrink-0">
          <AlertCircle className="h-3.5 w-3.5 text-amber-400" />
        </div>
        <div>
          <p className="text-xs font-medium text-foreground">No repository linked</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
            Select a repository in the Git module to link it with this org's metadata.
          </p>
        </div>
      </div>
      <a
        href="/studio/git"
        className="flex items-center justify-center gap-1.5 w-full px-3 py-1.5 rounded-md bg-muted text-muted-foreground text-xs font-medium hover:text-foreground hover:bg-muted/80 transition-colors"
      >
        <GitBranch className="h-3 w-3" />
        Go to Git module
      </a>
    </div>
  );
}

// ── Panel: connected ──────────────────────────────────────────────────────────

function ConnectedPanel({ repo, effectiveDefaultBranch, configuredDefaultBranch }: {
  repo: GitRepo;
  effectiveDefaultBranch: string | undefined;
  configuredDefaultBranch: GitBranch | null;
}) {
  return (
    <div className="p-3 space-y-2.5">
      {/* Header — icon + repo + meta inline */}
      <div className="flex items-center gap-2 min-w-0">
        <div className="h-6 w-6 rounded-md bg-emerald-500/10 flex items-center justify-center shrink-0">
          <Github className="h-3 w-3 text-emerald-400" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-mono text-emerald-400 truncate">{repo.full_name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-muted-foreground">{effectiveDefaultBranch ?? repo.default_branch}</span>
            <span className="text-muted-foreground/40">·</span>
            <span className="text-[10px] text-muted-foreground">{repo.private ? 'Private' : 'Public'}</span>
          </div>
        </div>
      </div>

      {/* Branch selector */}
      <BranchSection repoFullName={repo.full_name} workingBranchId={configuredDefaultBranch?.id ?? null} />

      <a
        href={repo.html_url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-1.5 w-full px-3 py-1.5 rounded-md bg-muted text-muted-foreground text-xs font-medium hover:text-foreground hover:bg-muted/80 transition-colors"
      >
        <ExternalLink className="h-3 w-3" />
        View on GitHub
      </a>
    </div>
  );
}

// ── Branch section ────────────────────────────────────────────────────────────

function BranchSection({ repoFullName, workingBranchId }: {
  repoFullName: string;
  workingBranchId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const { data: branches = [], isLoading } = useGitBranches(repoFullName);
  const { mutate: setDefault, isPending, variables } = useSetDefaultGitBranch();

  const listRef = useRef<HTMLDivElement>(null);
  const [measuredHeight, setMeasuredHeight] = useState(0);

  const workingBranch = branches.find((b) => b.id === workingBranchId) ?? null;

  useEffect(() => {
    if (listRef.current) {
      setMeasuredHeight(Math.min(listRef.current.scrollHeight, 160));
    }
  }, [branches, open]);

  const listSpring = useSpring({
    height: open ? measuredHeight : 0,
    opacity: open ? 1 : 0,
    config: { tension: 280, friction: 26, clamp: true },
  });

  const chevronSpring = useSpring({
    rotate: open ? 180 : 0,
    config: { tension: 280, friction: 26 },
  });

  function handleSelect(branch: GitBranch) {
    if (branch.id === null) return;
    const isClearing = branch.id === workingBranchId;
    setDefault({ gitBranchId: isClearing ? null : branch.id });
    setOpen(false);
  }

  return (
    <div className="rounded-md border border-border/60 overflow-hidden">
      {/* Trigger row — always visible, shows current selection */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 w-full px-2.5 py-1.5 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <GitBranch className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span className={[
          "text-[11px] flex-1 text-left truncate font-mono",
          workingBranch ? "text-foreground" : "text-muted-foreground italic",
        ].join(" ")}>
          {isLoading ? "Loading…" : workingBranch?.name ?? "No working branch"}
        </span>
        {isPending
          ? <Loader2 className="h-3 w-3 animate-spin shrink-0 text-muted-foreground" />
          : (
            <animated.div style={{ rotate: chevronSpring.rotate }}>
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </animated.div>
          )
        }
      </button>

      {/* Animated dropdown */}
      <animated.div
        style={{ ...listSpring, overflow: 'hidden' }}
        className="border-t border-border/60"
      >
        <div ref={listRef} className="max-h-40 overflow-y-auto">
          {branches.length === 0 && !isLoading && (
            <p className="px-2.5 py-2 text-[11px] text-muted-foreground">No branches found.</p>
          )}
          {branches.map((b) => {
            const isWorking = b.id !== null && b.id === workingBranchId;
            const isSettingThis = isPending && (variables?.gitBranchId === b.id || (isWorking && variables?.gitBranchId === null));
            return (
              <button
                key={b.name}
                type="button"
                disabled={b.id === null || isPending}
                onClick={() => handleSelect(b)}
                className={[
                  "flex items-center gap-2 w-full px-2.5 py-1.5 text-[11px] transition-colors",
                  isWorking
                    ? "bg-emerald-500/8 text-emerald-400"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/40",
                  b.id === null ? "opacity-40 cursor-not-allowed" : "",
                ].join(" ")}
              >
                <GitBranch className="h-3 w-3 shrink-0" />
                <span className="font-mono truncate flex-1 text-left">{b.name}</span>
                {isSettingThis
                  ? <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                  : isWorking && <CheckCircle2 className="h-3 w-3 shrink-0" />
                }
              </button>
            );
          })}
        </div>
      </animated.div>
    </div>
  );
}
